import Layout from '@/components/feature/Layout';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryTabs from './components/CategoryTabs';
import { useParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { useState, useMemo } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === slug),
    [slug]
  );

  const totalPages = Math.ceil(filteredArticles.length / pageSize);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category ? `${category.name} - 凤楼信息` : '分类页面',
    description: category?.description || '',
    url: `https://www.fenglou-info.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: paginatedArticles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Article',
          name: a.title,
          url: `https://www.fenglou-info.com/article/${a.id}`,
        },
      })),
    },
  };

  return (
    <Layout>
      <SEOMeta
        title={category ? `${category.name} - 凤楼信息` : '分类页面 - 凤楼信息'}
        description={category?.description || '浏览凤楼信息各分类内容'}
        keywords={`凤楼信息,${category?.name || ''},凤楼资讯,${category?.name || ''}分类`}
        canonicalUrl={`https://www.fenglou-info.com/category/${slug}`}
        schema={categorySchema}
        ogType="website"
      />

      <div className="pt-24 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类' },
            ]}
          />

          <div className="mt-6 mb-8">
            <CategoryTabs currentSlug={slug || ''} />
          </div>

          <h1
            className="text-2xl md:text-3xl font-bold text-foreground-900 mt-4"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {category?.name || '分类页面'}
          </h1>
          <p className="text-foreground-500 mt-2 text-sm leading-relaxed max-w-2xl">
            {category?.description}
          </p>

          {filteredArticles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                {paginatedArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                      currentPage === 1
                        ? 'text-foreground-300 bg-background-100 cursor-not-allowed'
                        : 'text-foreground-600 bg-background-100 hover:bg-background-200'
                    }`}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                        currentPage === page
                          ? 'bg-primary-500 text-background-50 font-medium'
                          : 'text-foreground-600 bg-background-100 hover:bg-background-200'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                      currentPage === totalPages
                        ? 'text-foreground-300 bg-background-100 cursor-not-allowed'
                        : 'text-foreground-600 bg-background-100 hover:bg-background-200'
                    }`}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-2xl bg-background-100 text-foreground-400 mb-4">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂无相关文章</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}